/**
 * Library Store - Manages documents/materials state
 */

import { create } from 'zustand';
import { libraryApi, Material } from '../services/api';

// Re-export Material type for convenience
export type { Material };

export type UploadPhase = 'idle' | 'uploading' | 'queued' | 'processing' | 'completed' | 'error';

interface LibraryState {
  materials: Material[];
  selectedMaterial: Material | null;
  isLoading: boolean;
  error: string | null;
  uploadProgress: number;
  uploadSessionId: string | null;
  uploadFileKey: string | null;
  uploadOverlayVisible: boolean;
  uploadPhase: UploadPhase;
  uploadStage: string;

  // Actions
  fetchLibrary: (token: string, isCurrent?: () => boolean) => Promise<void>;
  selectMaterial: (material: Material | null) => void;
  uploadFile: (file: File, token: string, isCurrent?: () => boolean) => Promise<string>;
  pollUploadStatus: (sessionId: string, getToken: () => Promise<string | null>, isCurrent?: () => boolean) => Promise<Material | null>;
  deleteMaterial: (materialId: string, token: string) => Promise<void>;
  setUploadProgress: (progress: number) => void;
  dismissUploadOverlay: () => void;
  clearError: () => void;
}

type ActiveUploadRequest = {
  fileKey: string;
  promise: Promise<string>;
};

const getUploadFileKey = (file: File) => [
  file.name,
  file.size,
  file.lastModified,
  file.type,
].join('\u0000');

// These guards live outside React components so every upload entry point
// (sidebar, mobile drawer, and context menu) shares the same in-flight work.
let activeUploadRequest: ActiveUploadRequest | null = null;
const activeUploadStatusPolls = new Map<string, Promise<Material | null>>();

const useLibraryStoreBase = create<LibraryState>((set, get) => ({
  materials: [],
  selectedMaterial: null,
  isLoading: false,
  error: null,
  uploadProgress: 0,
  uploadSessionId: null,
  uploadFileKey: null,
  uploadOverlayVisible: false,
  uploadPhase: 'idle',
  uploadStage: '',

  fetchLibrary: async (token: string, isCurrent = () => true) => {
    if (!isCurrent()) return;
    set({ isLoading: true, error: null });
    try {
      const response = await libraryApi.getLibrary(token);
      if (!isCurrent()) return;
      set({ materials: response.materials || [], isLoading: false });
    } catch (error) {
      if (!isCurrent()) return;
      set({ error: (error as Error).message, isLoading: false, materials: [] });
    }
  },

  selectMaterial: (material: Material | null) => {
    set({ selectedMaterial: material });
  },

  uploadFile: async (file: File, token: string, isCurrent = () => true) => {
    if (!isCurrent()) throw new Error('Upload owner changed');
    const fileKey = getUploadFileKey(file);

    // A double click/drop can reach the store before React disables the
    // originating button. Reuse the first POST instead of creating a duplicate.
    if (activeUploadRequest) {
      if (activeUploadRequest.fileKey === fileKey) {
        return activeUploadRequest.promise;
      }

      const message = 'Another document is already uploading. Please wait for it to finish.';
      set({ error: message });
      throw new Error(message);
    }

    const currentUpload = get();
    const uploadIsActive = ['uploading', 'queued', 'processing'].includes(currentUpload.uploadPhase);
    if (uploadIsActive) {
      // If the same file is selected again after its POST finished, reuse its
      // server session. This keeps duplicate clicks from enqueueing a new job.
      if (currentUpload.uploadFileKey === fileKey && currentUpload.uploadSessionId) {
        set({ uploadOverlayVisible: true });
        return currentUpload.uploadSessionId;
      }

      const message = 'Another document is already uploading. Please wait for it to finish.';
      set({ error: message });
      throw new Error(message);
    }

    set({
      uploadProgress: 0,
      uploadSessionId: null,
      uploadFileKey: fileKey,
      uploadOverlayVisible: true,
      uploadPhase: 'uploading',
      uploadStage: 'Uploading your document...',
      error: null,
    });

    const staleOwner = () => {
      if (isCurrent()) return false;
      const state = get();
      if (state.uploadFileKey === fileKey && state.uploadSessionId === null && state.uploadPhase === 'uploading') {
        set({ uploadSessionId: null, uploadFileKey: null, uploadOverlayVisible: false,
          uploadPhase: 'idle', uploadStage: '', uploadProgress: 0 });
      }
      return true;
    };
    const request = libraryApi.uploadFile(file, token, (progress) => {
      if (staleOwner()) return;
      set({ uploadProgress: progress, uploadStage: 'Uploading your document...' });
    }).then((response) => {
      if (staleOwner()) return response.sessionId;
      set({
        uploadSessionId: response.sessionId,
        uploadProgress: 0,
        uploadPhase: 'queued',
        uploadStage: 'Waiting for document processing...',
      });
      return response.sessionId;
    }).catch((error) => {
      if (staleOwner()) throw error;
      set({
        error: (error as Error).message,
        uploadProgress: 0,
        uploadSessionId: null,
        uploadFileKey: null,
        uploadOverlayVisible: false,
        uploadPhase: 'error',
        uploadStage: '',
      });
      throw error;
    });

    activeUploadRequest = { fileKey, promise: request };

    try {
      return await request;
    } finally {
      if (activeUploadRequest?.promise === request) {
        activeUploadRequest = null;
      }
    }
  },

  pollUploadStatus: async (sessionId: string, getToken: () => Promise<string | null>, isCurrent = () => true) => {
    const activePoll = activeUploadStatusPolls.get(sessionId);
    if (activePoll) {
      return activePoll;
    }

    const pollInterval = 2000;
    const maxAttempts = 150; // 5 minutes
    let attempts = 0;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollingPromise = new Promise<Material | null>((resolve, reject) => {
      const canContinue = () => {
        if (!isCurrent()) {
          // Unblock a subsequent account's uploads, but never clear a newer
          // upload that already replaced this old owner's session.
          if (get().uploadSessionId === sessionId) {
            set({ uploadSessionId: null, uploadFileKey: null, uploadOverlayVisible: false,
              uploadPhase: 'idle', uploadStage: '', uploadProgress: 0 });
          }
          resolve(null);
          return false;
        }
        if (get().uploadSessionId !== sessionId) {
          resolve(null);
          return false;
        }
        return true;
      };
      const failPolling = (message: string) => {
        if (!canContinue()) return;
        if (get().uploadSessionId === sessionId) {
          set({
            error: message,
            uploadSessionId: null,
            uploadFileKey: null,
            uploadOverlayVisible: false,
            uploadPhase: 'error',
            uploadStage: '',
          });
        }
        reject(new Error(message));
      };

      const poll = async () => {
        // A newer upload replaced this session, so this poller must not update
        // or clear the newer upload's UI state.
        if (!canContinue()) return;

        try {
          // Get a fresh token each poll to avoid expiry
          const freshToken = await getToken();
          if (!canContinue()) return;
          if (!freshToken) {
            consecutiveErrors++;
            if (consecutiveErrors >= maxConsecutiveErrors) {
              failPolling('Authentication lost during upload. Please refresh and check your library.');
              return;
            }
            setTimeout(poll, pollInterval);
            return;
          }

          const status = await libraryApi.getUploadStatus(sessionId, freshToken);
          if (!canContinue()) return;
          consecutiveErrors = 0; // Reset on success

          const normalizedStatus = status.uploadStatus;

          if (normalizedStatus === 'completed') {
            // Refresh library
            await get().fetchLibrary(freshToken, () => isCurrent() && get().uploadSessionId === sessionId);
            if (!canContinue()) return;
            const material = status.materialId
              ? get().materials.find(m => m.id === status.materialId)
              : null;

            if (get().uploadSessionId === sessionId) {
              set({
                uploadProgress: 100,
                uploadSessionId: null,
                uploadFileKey: null,
                uploadOverlayVisible: false,
                uploadPhase: 'completed',
                uploadStage: status.stage || 'Upload complete',
              });
            }
            resolve(material || null);
            return;
          }

          if (['error', 'failed', 'cancelled', 'canceled'].includes(normalizedStatus)) {
            failPolling(status.error || status.details || status.stage || 'Upload processing failed');
            return;
          }

          const reportedProgress = Number.isFinite(status.progress)
            ? Math.max(0, Math.min(99, status.progress))
            : 0;
          const isQueued = normalizedStatus === 'queued';

          set({
            uploadProgress: reportedProgress,
            uploadPhase: isQueued ? 'queued' : 'processing',
            uploadStage: status.stage || (
              isQueued
                ? 'Waiting for document processing...'
                : 'Processing your document...'
            ),
          });
          
          attempts++;
          if (attempts >= maxAttempts) {
            failPolling('Upload is taking longer than expected. Please refresh and check your library.');
            return;
          }

          setTimeout(poll, pollInterval);
        } catch (error) {
          if (!canContinue()) return;
          // Tolerate transient errors — only fail after consecutive failures
          consecutiveErrors++;
          console.warn(`Poll attempt ${attempts} failed (${consecutiveErrors}/${maxConsecutiveErrors}):`, error);
          
          if (consecutiveErrors >= maxConsecutiveErrors) {
            failPolling('Lost connection during upload. Please refresh to check your library.');
            return;
          }

          // Retry after a slightly longer delay
          attempts++;
          if (attempts >= maxAttempts) {
            failPolling('Upload is taking longer than expected. Please refresh and check your library.');
            return;
          }
          setTimeout(poll, pollInterval * 2);
        }
      };

      poll();
    });

    activeUploadStatusPolls.set(sessionId, pollingPromise);
    try {
      return await pollingPromise;
    } finally {
      if (activeUploadStatusPolls.get(sessionId) === pollingPromise) {
        activeUploadStatusPolls.delete(sessionId);
      }
    }
  },

  deleteMaterial: async (materialId: string, token: string) => {
    try {
      await libraryApi.deleteMaterial(materialId, token);
      set((state) => ({
        materials: state.materials.filter((m) => m.id !== materialId),
        selectedMaterial:
          state.selectedMaterial?.id === materialId ? null : state.selectedMaterial,
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  setUploadProgress: (progress: number) => {
    set({ uploadProgress: progress });
  },

  dismissUploadOverlay: () => {
    set({ uploadOverlayVisible: false });
  },

  clearError: () => {
    set({ error: null });
  },
}));

export const getLibraryState = useLibraryStoreBase.getState;

// Safe hook that guarantees materials is always an array
export const useLibraryStore = () => {
  const state = useLibraryStoreBase();
  return {
    ...state,
    // Guarantee materials is always an array
    materials: Array.isArray(state.materials) ? state.materials : [],
    // Computed: is an upload in progress
    isUploading: state.uploadPhase === 'uploading' || state.uploadSessionId !== null,
    // Processing continues after dismissal; only the blocking overlay closes.
    showUploadOverlay: state.uploadOverlayVisible
      && (state.uploadPhase === 'uploading' || state.uploadSessionId !== null),
  };
};
